import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Globe2, TrendingUp, RefreshCw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_COLORS = {
  carrier: 'bg-blue-500',
  broker:  'bg-purple-500',
  shipper: 'bg-emerald-500',
};
const TYPE_TEXT = {
  carrier: 'text-blue-300',
  broker:  'text-purple-300',
  shipper: 'text-emerald-300',
};

const WaitlistAnalyticsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/waitlist/analytics?days=${days}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <Card data-testid="waitlist-analytics-loading">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading waitlist analytics…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const maxDay = Math.max(1, ...data.series.map((d) => d.total));

  return (
    <Card data-testid="waitlist-analytics-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-400" /> Waitlist Analytics
          </CardTitle>
          <p className="text-[11px] text-muted-foreground mt-1">
            Soft-launch traction — signups per day, segment mix, geo.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              data-testid={`range-${d}`}
            >
              {d}d
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} data-testid="waitlist-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Top tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile testId="total-tile" label="Total (all-time)" value={data.total_all_time} />
          <Tile testId="window-tile" label={`Last ${data.window_days}d`} value={data.window_total} icon={TrendingUp} />
          <Tile testId="carriers-tile" label="Carriers" value={data.by_user_type.carrier}
                color="text-blue-300" />
          <Tile testId="brokers-tile"  label="Brokers / Shippers"
                value={data.by_user_type.broker + data.by_user_type.shipper}
                color="text-purple-300"
                subtitle={`${data.by_user_type.broker} brokers · ${data.by_user_type.shipper} shippers`} />
        </div>

        {/* Daily signups stacked bars */}
        <div data-testid="daily-chart">
          <div className="text-xs font-semibold text-zinc-300 mb-2">Signups per day</div>
          <div className="flex items-end gap-0.5 h-32 rounded-md border border-zinc-800 bg-zinc-900/40 p-2">
            {data.series.map((d) => {
              const total = d.total;
              const h = total === 0 ? 2 : Math.max(6, Math.round((total / maxDay) * 100));
              const segs = [
                { k: 'carrier', n: d.carrier },
                { k: 'broker',  n: d.broker  },
                { k: 'shipper', n: d.shipper },
              ];
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-px"
                     title={`${d.date}: ${total} (${d.carrier}c/${d.broker}b/${d.shipper}s)`}
                     data-testid={`waitlist-bar-${d.date}`}>
                  <div className="w-full flex flex-col-reverse rounded-sm overflow-hidden"
                       style={{ height: `${h}%` }}>
                    {segs.filter((s) => s.n > 0).map((s) => (
                      <div key={s.k}
                           className={`${TYPE_COLORS[s.k]} opacity-90`}
                           style={{ flex: s.n }} />
                    ))}
                    {total === 0 && <div className="w-full h-full bg-zinc-800/50" />}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[11px] text-zinc-400">
            <Legend color="bg-blue-500" label="Carrier" />
            <Legend color="bg-purple-500" label="Broker" />
            <Legend color="bg-emerald-500" label="Shipper" />
          </div>
        </div>

        {/* Geo + recent feed */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-md border border-zinc-800 p-3" data-testid="geo-card">
            <div className="text-xs font-semibold text-zinc-300 flex items-center gap-2 mb-2">
              <Globe2 className="w-3.5 h-3.5 text-zinc-400" />
              Top geographies (last {data.window_days}d)
            </div>
            {data.top_countries.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">
                No geo data — set Cloudflare CF-IPCountry passthrough for richer info.
              </div>
            ) : (
              <ul className="space-y-1">
                {data.top_countries.map((c, i) => (
                  <li key={c.country + i} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300">{c.country}</span>
                    <span className="text-zinc-500 tabular-nums">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-md border border-zinc-800 p-3" data-testid="recent-card">
            <div className="text-xs font-semibold text-zinc-300 mb-2">Recent signups</div>
            {data.recent.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">No signups yet.</div>
            ) : (
              <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {data.recent.map((r) => (
                  <li key={r.email + r.user_type} className="flex items-start gap-2 text-xs">
                    <Badge variant="outline" className={`shrink-0 ${TYPE_TEXT[r.user_type] || ''}`}>
                      {r.user_type}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-zinc-200">{r.name || r.email}</div>
                      <div className="truncate text-[11px] text-zinc-500">
                        {r.email}{r.company ? ` · ${r.company}` : ''}
                      </div>
                    </div>
                    <div className="text-[10px] text-zinc-500 shrink-0">
                      {new Date(r.joined_at).toLocaleDateString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Tile = ({ label, value, color = 'text-foreground', icon: Icon, subtitle, testId }) => (
  <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
      {Icon ? <Icon className="w-3 h-3" /> : null} {label}
    </div>
    <div className={`text-2xl font-bold mt-1 tabular-nums ${color}`}>{value}</div>
    {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</div>}
  </div>
);

const Legend = ({ color, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`w-2.5 h-2.5 rounded-sm ${color}`} /> {label}
  </span>
);

export default WaitlistAnalyticsWidget;
